import { useMemo, useState } from 'react';
import type { ChangeEvent, JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { dateTime, num } from '../lib/format.js';
import {
  BROADCAST_STATUS_LABEL,
  BROADCAST_STATUS_TONE,
  PLAN_KIND_LABEL,
  PLAN_RACE_MATCH_LABEL,
  PLAN_STATUS_LABEL,
  PLAN_STATUS_TONE,
  label,
} from '../lib/labels.js';
import { useMe } from '../lib/session.js';
import type { Rights } from '../lib/session.js';
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  LoadingState,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-broadcasts.css';

/**
 * Plán rozesílek (docs/ROZESILKY.md kap. 9): týdenní plán mailingu z tabulky specialistky. Položka je jen plán,
 * nic neodesílá. „Vytvořit koncept“ založí rozesílku, která pak prochází běžným schvalováním v Rozesílkách.
 */

interface PlanItem {
  id: number;
  weekStart: string;
  weekEnd: string;
  raceLabel: string;
  season: number | null;
  raceId: string | null;
  raceName: string | null;
  raceMatch: string;
  topic: string;
  kind: string;
  note: string | null;
  status: string;
  storedStatus: string;
  brand: string | null;
  languages: string[] | null;
  owner: { id: string; name: string } | null;
  broadcast: {
    id: number;
    code: string;
    name: string;
    status: string;
    holdReason: string | null;
    scheduledAt: string | null;
  } | null;
  alerts: Array<{ code: string; text: string }>;
  suggested: { brand: string | null; languages: string[]; scheduledAt: string | null };
}

/** Název závodu do výběru: série, název a ročník (název už ročník často obsahuje). */
export const raceOptionLabel = (r: { series: string; name: string; season: number }): string => {
  const name = r.name.includes(String(r.season)) ? r.name : `${r.name} ${r.season}`;
  return name.toLowerCase().includes(r.series.toLowerCase())
    ? name
    : `${r.series.toUpperCase()} ${name}`;
};

interface PlanResponse {
  items: PlanItem[];
  counts: Record<string, number>;
  defaultYear: number;
  today: string;
}

interface PreviewRow {
  line: number;
  week: { start: string; end: string; yearAssumed: boolean } | null;
  raceLabel: string;
  season: number | null;
  topic: string;
  note: string | null;
  kind: string;
  errors: string[];
  warnings: string[];
  action: 'new' | 'existing' | 'duplicate' | 'invalid';
  race: { state: string; name?: string; candidates?: Array<{ raceId: string; name: string }> };
}

interface ImportPreview {
  year: number;
  rows: PreviewRow[];
  error?: string;
  summary: {
    total: number;
    new: number;
    existing: number;
    duplicate: number;
    invalid: number;
    raceNotFound: number;
  };
}

const ACTION_LABEL: Record<PreviewRow['action'], string> = {
  new: 'Uloží se',
  existing: 'Už v plánu',
  duplicate: 'Duplicita v textu',
  invalid: 'Chyba',
};
const ACTION_TONE: Record<PreviewRow['action'], 'ok' | 'warn' | 'bad' | 'neu'> = {
  new: 'ok',
  existing: 'neu',
  duplicate: 'warn',
  invalid: 'bad',
};

const SEGMENT_TEMPLATE_LABEL: Record<string, string> = {
  race_interest: 'Zájemci o závod',
  interested_no_purchase: 'Zájemci bez nákupu',
  recent_series_buyers: 'Nedávní kupující série',
};

/** `2026-10-19`, `2026-10-25` -> `19.–25. 10. 2026`. */
export function weekLabel(start: string, end: string): string {
  const [y1, m1, d1] = start.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = end.split('-').map(Number) as [number, number, number];
  if (start === end) return `${d1}. ${m1}. ${y1}`;
  if (y1 === y2 && m1 === m2) return `${d1}.–${d2}. ${m1}. ${y1}`;
  if (y1 === y2) return `${d1}. ${m1}.–${d2}. ${m2}. ${y1}`;
  return `${d1}. ${m1}. ${y1}–${d2}. ${m2}. ${y2}`;
}

const errorText = (err: unknown): string =>
  err instanceof Error ? err.message : 'Něco se nepovedlo. Zkuste to znovu.';

export function BroadcastPlan({
  rights,
  onOpenBroadcast,
}: {
  rights: Rights;
  onOpenBroadcast: (id: number) => void;
}): JSX.Element {
  const client = useQueryClient();
  const me = useMe();
  const canWrite = rights.write('communication');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [brand, setBrand] = useState('');
  const [raceId, setRaceId] = useState('');
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<PlanItem | null>(null);
  const [drafting, setDrafting] = useState<PlanItem | null>(null);
  const [deleting, setDeleting] = useState<PlanItem | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const filters = { status, kind, brand, raceId };
  const query = useQuery({
    queryKey: ['broadcast-plan', filters],
    queryFn: () => api.get<PlanResponse>(`/api/broadcast-plan${qs(filters)}`),
    refetchInterval: 30_000,
    retry: false,
  });
  const races = useQuery({
    queryKey: ['broadcast-plan-races'],
    queryFn: () =>
      api.get<{
        items: Array<{
          raceId: string;
          name: string;
          season: number;
          series: string;
          brand: string | null;
        }>;
      }>('/api/broadcast-plan/race-options'),
  });
  const refresh = (): void => void client.invalidateQueries({ queryKey: ['broadcast-plan'] });

  const weeks = useMemo(() => {
    const out = new Map<string, { label: string; items: PlanItem[] }>();
    for (const it of query.data?.items ?? []) {
      const key = `${it.weekStart}|${it.weekEnd}`;
      const w = out.get(key) ?? { label: weekLabel(it.weekStart, it.weekEnd), items: [] };
      w.items.push(it);
      out.set(key, w);
    }
    return [...out.entries()];
  }, [query.data]);

  if (query.isLoading) return <LoadingState what="plán rozesílek" />;
  if (query.isError) {
    const disabled = query.error instanceof ApiError && query.error.code === 'layer_disabled';
    return disabled ? (
      <EmptyState title="Vrstva Marketingové odesílání je vypnutá" icon="layers">
        Plán rozesílek je součástí vlny 1. Zapíná ji administrátor v Nastavení → Vlny nasazení.
      </EmptyState>
    ) : (
      <ErrorState
        title="Plán rozesílek se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const data = query.data!;
  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);
  const attention = data.items.filter((i) => i.alerts.length > 0).length;

  return (
    <>
      <Panel
        title="Plán rozesílek"
        icon="calendar"
        subtitle="Týdenní plán mailingu: co, kdy a k jakému závodu odejde. Položka nic neodesílá. Z položky založíte koncept rozesílky, který pak schvaluje jiný člověk v záložce Rozesílky."
        action={
          canWrite ? (
            <button className="btn pri" onClick={() => setImporting(true)}>
              <Icon name="file" size={14} /> Importovat z tabulky
            </button>
          ) : null
        }
      >
        {note ? (
          <p className={`note ${note.tone === 'bad' ? 'bad' : ''}`} role="status">
            {note.text}
          </p>
        ) : null}
        <div className="bcform" role="group" aria-label="Filtry plánu">
          <Field label="Stav">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Všechny stavy ({num(total)})</option>
              {Object.entries(PLAN_STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v} ({num(data.counts[k] ?? 0)})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Typ rozesílky">
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Všechny typy</option>
              {Object.entries(PLAN_KIND_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Závod">
            <select value={raceId} onChange={(e) => setRaceId(e.target.value)}>
              <option value="">Všechny závody</option>
              {(races.data?.items ?? []).map((r) => (
                <option key={r.raceId} value={r.raceId}>
                  {raceOptionLabel(r)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Značka">
            <BrandSelect value={brand} onChange={setBrand} />
          </Field>
        </div>
        {attention > 0 ? (
          <p className="note" role="status">
            <Icon name="alert" size={14} /> {num(attention)}{' '}
            {attention === 1 ? 'položka vyžaduje' : 'položek vyžaduje'} pozornost (blížící se týden
            bez rozesílky, nepřiřazený závod nebo zadržená rozesílka).
          </p>
        ) : null}

        {weeks.length === 0 ? (
          <EmptyState title="Plán je prázdný" icon="calendar">
            {canWrite
              ? 'Vložte řádky z tabulky tlačítkem Importovat z tabulky (sloupce Týden, Závod, Ročník, Téma rozesílky, Poznámka).'
              : 'Plán zakládá mailing specialista nebo administrátor.'}
          </EmptyState>
        ) : (
          weeks.map(([key, week]) => (
            <section key={key} aria-label={`Týden ${week.label}`}>
              <h3 className="planweek">
                Týden {week.label}
                {key.split('|')[0]! <= data.today && key.split('|')[1]! >= data.today ? (
                  <StatusBadge tone="acc">Probíhá</StatusBadge>
                ) : null}
              </h3>
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Závod</th>
                      <th>Téma rozesílky</th>
                      <th>Typ</th>
                      <th>Stav</th>
                      <th>Rozesílka</th>
                      <th>Odpovídá</th>
                      <th>
                        <span className="sr-only">Akce</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {week.items.map((it) => (
                      <tr key={it.id}>
                        <td>
                          <b>{it.raceName ?? it.raceLabel}</b>{' '}
                          {it.season && !(it.raceName ?? '').includes(String(it.season))
                            ? it.season
                            : ''}
                          {it.raceMatch !== 'matched' && it.raceMatch !== 'manual' ? (
                            <>
                              <br />
                              <StatusBadge tone="warn">
                                {label(PLAN_RACE_MATCH_LABEL, it.raceMatch)}
                              </StatusBadge>
                            </>
                          ) : null}
                        </td>
                        <td>
                          {it.topic}
                          {it.note ? (
                            <>
                              <br />
                              <small className="muted">{it.note}</small>
                            </>
                          ) : null}
                        </td>
                        <td>{label(PLAN_KIND_LABEL, it.kind)}</td>
                        <td>
                          <StatusBadge tone={PLAN_STATUS_TONE[it.status] ?? 'neu'}>
                            {label(PLAN_STATUS_LABEL, it.status)}
                          </StatusBadge>
                          {it.alerts.map((a) => (
                            <div key={a.code}>
                              <StatusBadge tone="warn">{a.text}</StatusBadge>
                            </div>
                          ))}
                        </td>
                        <td>
                          {it.broadcast ? (
                            <>
                              <button
                                className="btn"
                                onClick={() => onOpenBroadcast(it.broadcast!.id)}
                                aria-label={`Otevřít rozesílku ${it.broadcast.code}`}
                              >
                                {it.broadcast.code}
                              </button>
                              <br />
                              <StatusBadge
                                tone={BROADCAST_STATUS_TONE[it.broadcast.status] ?? 'neu'}
                              >
                                {label(BROADCAST_STATUS_LABEL, it.broadcast.status)}
                              </StatusBadge>
                              {it.broadcast.scheduledAt ? (
                                <small className="muted">
                                  {' '}
                                  {dateTime(it.broadcast.scheduledAt, me.data?.defaultTimezone)}
                                </small>
                              ) : null}
                            </>
                          ) : (
                            <span className="muted">Zatím žádná</span>
                          )}
                        </td>
                        <td>{it.owner?.name ?? <span className="muted">nikdo</span>}</td>
                        <td>
                          {canWrite ? (
                            <div className="row bcactions">
                              {!it.broadcast &&
                              it.status !== 'cancelled' &&
                              it.status !== 'sent' ? (
                                <button className="btn pri" onClick={() => setDrafting(it)}>
                                  Vytvořit koncept rozesílky
                                </button>
                              ) : null}
                              <button className="btn" onClick={() => setEditing(it)}>
                                Upravit
                              </button>
                              {!it.broadcast ? (
                                <button
                                  className="btn dng"
                                  onClick={() => setDeleting(it)}
                                  aria-label={`Smazat položku ${it.topic}`}
                                >
                                  Smazat
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </Panel>

      <ImportDialog
        open={importing}
        defaultYear={data.defaultYear}
        onClose={() => setImporting(false)}
        onDone={(text) => {
          setNote({ tone: 'ok', text });
          setImporting(false);
          refresh();
        }}
      />
      {editing ? (
        <EditDialog
          item={editing}
          races={races.data?.items ?? []}
          myId={me.data?.user.id ?? null}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNote({ tone: 'ok', text: 'Položka uložena.' });
            refresh();
          }}
        />
      ) : null}
      {drafting ? (
        <DraftDialog
          item={drafting}
          onClose={() => setDrafting(null)}
          onDone={(r) => {
            setDrafting(null);
            setNote({
              tone: 'ok',
              text: `Koncept ${r.code} je založen. ${r.todo.length > 0 ? `Zbývá doplnit: ${r.todo.join(' ')}` : ''} Rozesílku otevřete tlačítkem s kódem v řádku.`,
            });
            refresh();
            void client.invalidateQueries({ queryKey: ['broadcasts'] });
          }}
        />
      ) : null}
      <DeleteConfirm
        item={deleting}
        onClose={() => setDeleting(null)}
        onDone={() => {
          setDeleting(null);
          setNote({ tone: 'ok', text: 'Položka smazána.' });
          refresh();
        }}
        onError={(m) => {
          setDeleting(null);
          setNote({ tone: 'bad', text: m });
        }}
      />
    </>
  );
}

/* --------------------------------------------------------------- import --- */

function ImportDialog({
  open,
  defaultYear,
  onClose,
  onDone,
}: {
  open: boolean;
  defaultYear: number;
  onClose: () => void;
  onDone: (text: string) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [year, setYear] = useState<string>(String(defaultYear));
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const body = (): { text: string; year?: number } => ({
    text,
    ...(/^\d{4}$/.test(year) ? { year: Number(year) } : {}),
  });
  const check = useMutation({
    mutationFn: () => api.post<ImportPreview>('/api/broadcast-plan/import/preview', body()),
    onSuccess: (r) => {
      setPreview(r);
      setError(r.error ?? null);
    },
    onError: (err) => setError(errorText(err)),
  });
  const save = useMutation({
    mutationFn: () =>
      api.post<{ created: number; existing: number; invalid: number; duplicate: number }>(
        '/api/broadcast-plan/import',
        body(),
      ),
    onSuccess: (r) => {
      onDone(
        `Import hotov: uloženo ${num(r.created)}, už v plánu ${num(r.existing)}, s chybou ${num(r.invalid)}.`,
      );
      setText('');
      setPreview(null);
    },
    onError: (err) => setError(errorText(err)),
  });
  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    void file.text().then((t) => {
      setText(t);
      setPreview(null);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} title="Import plánu z tabulky" wide>
      <p className="note">
        Zkopírujte řádky z tabulky (se záhlavím Týden, Závod, Ročník, Téma rozesílky, Poznámka) a
        vložte je sem, nebo nahrajte soubor CSV. Nejdřív se jen zkontroluje, co se rozpoznalo;
        ukládá se až po potvrzení.
      </p>
      <div className="bcform">
        <Field
          label="Rok týdnů"
          hint="Platí pro týdny, které nemají v tabulce rok (např. 19.–25. 10.)."
        >
          <input
            value={year}
            inputMode="numeric"
            maxLength={4}
            onChange={(e) => {
              setYear(e.target.value);
              setPreview(null);
            }}
          />
        </Field>
        <Field label="Soubor CSV (nepovinné)">
          <input type="file" accept=".csv,text/csv,text/plain,.tsv" onChange={onFile} />
        </Field>
      </div>
      <Field label="Vložený text z tabulky">
        <textarea
          rows={8}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setPreview(null);
          }}
          placeholder={'Týden\tZávod\tRočník\tTéma rozesílky\tPoznámka'}
        />
      </Field>
      {error ? (
        <p className="note bad" role="alert">
          {error}
        </p>
      ) : null}
      {preview && !preview.error ? (
        <>
          <p className="note" role="status">
            {num(preview.summary.total)} řádků: {num(preview.summary.new)} nových,{' '}
            {num(preview.summary.existing)} už v plánu, {num(preview.summary.invalid)} s chybou
            {preview.summary.duplicate > 0
              ? `, ${num(preview.summary.duplicate)} duplicit v textu`
              : ''}
            . Závod chybí v kalendáři u {num(preview.summary.raceNotFound)} nových řádků (přiřadíte
            ho ručně po uložení).
          </p>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="n">Řádek</th>
                  <th>Týden</th>
                  <th>Závod</th>
                  <th>Téma</th>
                  <th>Typ</th>
                  <th>Výsledek</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.line}>
                    <td className="n">{r.line}</td>
                    <td>{r.week ? weekLabel(r.week.start, r.week.end) : '–'}</td>
                    <td>
                      {r.raceLabel} {r.season ?? ''}
                      {r.errors.length === 0 ? (
                        <>
                          <br />
                          <small className="muted">
                            {r.race.state === 'matched'
                              ? `Kalendář: ${r.race.name ?? ''}`
                              : label(PLAN_RACE_MATCH_LABEL, r.race.state)}
                          </small>
                        </>
                      ) : null}
                    </td>
                    <td>{r.topic}</td>
                    <td>{label(PLAN_KIND_LABEL, r.kind)}</td>
                    <td>
                      <StatusBadge tone={ACTION_TONE[r.action]}>
                        {ACTION_LABEL[r.action]}
                      </StatusBadge>
                      {[...r.errors, ...r.warnings].map((m) => (
                        <div key={m}>
                          <small className="muted">{m}</small>
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      <div className="row bcactions">
        <button
          className="btn"
          onClick={() => check.mutate()}
          disabled={text.trim() === '' || check.isPending}
        >
          Zkontrolovat
        </button>
        <button
          className="btn pri"
          onClick={() => save.mutate()}
          disabled={!preview || preview.summary.new === 0 || save.isPending}
        >
          Uložit {preview ? num(preview.summary.new) : ''} nových řádků
        </button>
        <button className="btn" onClick={onClose}>
          Zavřít
        </button>
      </div>
    </Dialog>
  );
}

/* --------------------------------------------------------------- úprava --- */

function EditDialog({
  item,
  races,
  myId,
  onClose,
  onDone,
}: {
  item: PlanItem;
  races: Array<{ raceId: string; name: string; season: number; series: string }>;
  myId: string | null;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [topic, setTopic] = useState(item.topic);
  const [note, setNote] = useState(item.note ?? '');
  const [kind, setKind] = useState(item.kind);
  const [raceId, setRaceId] = useState(item.raceId ?? '');
  const [brand, setBrand] = useState(item.brand ?? '');
  const [languages, setLanguages] = useState((item.languages ?? []).join(', '));
  const [status, setStatus] = useState(item.storedStatus);
  const [mine, setMine] = useState(item.owner?.id === myId && myId !== null);
  const [error, setError] = useState<string | null>(null);
  const locked = item.broadcast !== null;

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/broadcast-plan/${item.id}`, {
        topic,
        note: note.trim() === '' ? null : note,
        kind,
        raceId: raceId === '' ? null : raceId,
        brand: brand === '' ? null : brand,
        languages: languages.trim() === '' ? null : languages.split(/[\s,;]+/).filter(Boolean),
        ownerId: mine ? myId : null,
        ...(locked && status !== 'cancelled' && item.storedStatus !== 'cancelled'
          ? {}
          : { status }),
      }),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  });

  return (
    <Dialog open onClose={onClose} title={`Upravit položku plánu: ${item.topic}`}>
      <div className="bcform">
        <Field label="Téma rozesílky">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={300} />
        </Field>
        <Field label="Typ rozesílky" hint="Odvozeno z poznámky; můžete změnit.">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(PLAN_KIND_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Závod v kalendáři hubu"
          hint={`Z tabulky: ${item.raceLabel} ${item.season ?? ''}. ${label(PLAN_RACE_MATCH_LABEL, item.raceMatch)}.`}
        >
          <select value={raceId} onChange={(e) => setRaceId(e.target.value)}>
            <option value="">Nepřiřazeno</option>
            {races.map((r) => (
              <option key={r.raceId} value={r.raceId}>
                {raceOptionLabel(r)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Značka" hint="Prázdné = podle závodu.">
          <BrandSelect value={brand} onChange={setBrand} allLabel="Podle závodu" />
        </Field>
        <Field label="Jazyky" hint="Např. cs, en. Prázdné = jazyky značky.">
          <input value={languages} onChange={(e) => setLanguages(e.target.value)} />
        </Field>
        <Field
          label="Stav"
          hint={
            locked
              ? 'Stav plyne ze stavu rozesílky; ručně lze jen položku zrušit.'
              : 'Ruční stav položky bez rozesílky.'
          }
        >
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            {Object.entries(PLAN_STATUS_LABEL)
              .filter(([k]) => k !== 'scheduled' || status === 'scheduled')
              .map(([k, v]) => (
                <option
                  key={k}
                  value={k}
                  disabled={locked && k !== 'cancelled' && k !== item.storedStatus}
                >
                  {v}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Poznámka">
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        <label className="bcchk row">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          <span>Odpovídám za to já</span>
        </label>
      </div>
      {error ? (
        <p className="note bad" role="alert">
          {error}
        </p>
      ) : null}
      <div className="row bcactions">
        <button
          className="btn pri"
          onClick={() => save.mutate()}
          disabled={save.isPending || topic.trim() === ''}
        >
          Uložit
        </button>
        <button className="btn" onClick={onClose}>
          Zrušit
        </button>
      </div>
    </Dialog>
  );
}

/* --------------------------------------------------------------- koncept --- */

function DraftDialog({
  item,
  onClose,
  onDone,
}: {
  item: PlanItem;
  onClose: () => void;
  onDone: (r: { code: string; todo: string[] }) => void;
}): JSX.Element {
  const [brand, setBrand] = useState(item.suggested.brand ?? '');
  const [segmentTemplate, setSegmentTemplate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.post<{ code: string; todo: string[] }>(`/api/broadcast-plan/${item.id}/draft`, {
        ...(brand ? { brand } : {}),
        ...(segmentTemplate ? { segmentTemplate } : {}),
      }),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Dialog open onClose={onClose} title="Vytvořit koncept rozesílky">
      <p className="note">
        Založí se <b>koncept</b>: nic se neodešle ani neschválí. Název bude z tématu a závodu,
        navržený čas začátku týdne {item.suggested.scheduledAt ? 'je' : 'by byl'}{' '}
        {item.suggested.scheduledAt
          ? dateTime(item.suggested.scheduledAt)
          : '9:00 podle časového pásma značky (týden už začal, čas zůstane prázdný)'}
        . Šablonu a seznam doplníte v detailu rozesílky.
        {item.kind === 'reminder_availability'
          ? ' Rozesílka dostane pojistku: odešle se, jen když je na závod dostupná vstupenka.'
          : ''}
      </p>
      <div className="bcform">
        <Field label="Značka" hint="Povinná. Výchozí podle položky nebo závodu.">
          <BrandSelect value={brand} onChange={setBrand} allLabel="Vyberte značku" />
        </Field>
        <Field
          label="Seznam příjemců"
          hint="Nepovinné. Bez volby vznikne prázdný zástupný seznam, který vyměníte."
        >
          <select value={segmentTemplate} onChange={(e) => setSegmentTemplate(e.target.value)}>
            <option value="">Doplním sám(a) později</option>
            {Object.entries(SEGMENT_TEMPLATE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                Nový segment: {v}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error ? (
        <p className="note bad" role="alert">
          {error}
        </p>
      ) : null}
      <div className="row bcactions">
        <button
          className="btn pri"
          onClick={() => create.mutate()}
          disabled={create.isPending || brand === ''}
        >
          Vytvořit koncept
        </button>
        <button className="btn" onClick={onClose}>
          Zrušit
        </button>
      </div>
    </Dialog>
  );
}

function DeleteConfirm({
  item,
  onClose,
  onDone,
  onError,
}: {
  item: PlanItem | null;
  onClose: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const del = useMutation({
    mutationFn: (id: number) => api.del(`/api/broadcast-plan/${id}`),
    onSuccess: onDone,
    onError: (err) => onError(errorText(err)),
  });
  return (
    <ConfirmDialog
      open={item !== null}
      title="Smazat položku plánu?"
      confirmLabel="Smazat"
      danger
      busy={del.isPending}
      onConfirm={() => item && del.mutate(item.id)}
      onCancel={onClose}
    >
      Položka „{item?.topic}“ zmizí z plánu. Při dalším importu téže tabulky se ale vrátí. Chcete-li
      ji jen vyřadit, nastavte stav Zrušeno.
    </ConfirmDialog>
  );
}
